import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useBuyer } from "../contexts/BuyerContext";
import Header from "../components/Header";
import carddemo from "../carddemo.json";
import designerConfig from "../utils/designerConfig.json";
import { Save, Trash2, Plus, X, CheckCircle, Sparkles} from "lucide-react";
import { LAYOUTS } from "../components/layouts";
import LayoutSelector from "../components/layouts/LayoutSelector";
import FontPairSelector from "../components/layouts/FontPairSelector";
import BrandThemeExtractor from "../components/BrandThemeExtractor";
import "./VisualDesignerStudio.css";

/* ── Segment → relevant attribute keys ── */
const SEGMENT_ATTR_KEYS = {
  gold: ["purity_karat", "gold_color", "weight_grams", "hallmarked", "MC"],
  silver: [
    "purity",
    "weight_grams",
    "plating",
    "chain_length_inches",
    "anti_tarnish_coated",
    "clasp_type",
  ],
  diamond: [
    "carat_weight",
    "shape",
    "cut",
    "color",
    "clarity",
    "symmetry",
    "fluorescence",
    "certification",
    "certificate_number",
  ],
  platinum: [
    "purity",
    "weight_grams",
    "finish",
    "hypoallergenic",
    "engravable",
  ],
  gemstone: [
    "gemstone_type",
    "color",
    "carat_weight",
    "cut_shape",
    "dimensions_mm",
    "origin",
    "certification",
    "certificate_number",
  ],
};

const fmtAttr = (v) => {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v === null || v === undefined) return "—";
  return String(v);
};

const fmtPrice = (v) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(v);


/* ══ Theme Creator Modal ══ */
const PRESET_PALETTES = [
  {
    name: "Royal Gold",
    colors: {
      bg: "#0D1117",
      surface: "#161B22",
      text: "#E2C391",
      textMuted: "#8B949E",
      accent: "#D4AF37",
      border: "#30363D",
      primary: "#E2C391",
    },
  },
  {
    name: "Midnight Sapphire",
    colors: {
      bg: "#06101E",
      surface: "#0D1B2A",
      text: "#E0E7FF",
      textMuted: "#7D8C9E",
      accent: "#3B82F6",
      border: "#1B2A4A",
      primary: "#93C5FD",
    },
  },
  {
    name: "Champagne Velvet",
    colors: {
      bg: "#1C1917",
      surface: "#292524",
      text: "#FDE68A",
      textMuted: "#A8A29E",
      accent: "#F59E0B",
      border: "#44403C",
      primary: "#FDE68A",
    },
  },
  {
    name: "Emerald Luxury",
    colors: {
      bg: "#022C22",
      surface: "#064E3B",
      text: "#A7F3D0",
      textMuted: "#6EE7B7",
      accent: "#10B981",
      border: "#047857",
      primary: "#A7F3D0",
    },
  },
  {
    name: "Rose Romance",
    colors: {
      bg: "#4C0519",
      surface: "#881337",
      text: "#FBCFE8",
      textMuted: "#F472B6",
      accent: "#FB7185",
      border: "#BE123C",
      primary: "#FBCFE8",
    },
  },
  {
    name: "Pure Platinum",
    colors: {
      bg: "#09090B",
      surface: "#18181B",
      text: "#FAFAFA",
      textMuted: "#A1A1AA",
      accent: "#E4E4E7",
      border: "#27272A",
      primary: "#FFFFFF",
    },
  },
];

const generateRandomPalette = () => {
  // Use full spectrum of hues for more variety
  const chosenHue = Math.floor(Math.random() * 360);
  const isDark = Math.random() > 0.3;
  
 

  if (isDark) {
    // Dark themes with more variation
    const bgSat = 15 + Math.floor(Math.random() * 30);
    const bgLight = 5 + Math.floor(Math.random() * 10);
    const textSat = 30 + Math.floor(Math.random() * 20);
    const accentSat = 70 + Math.floor(Math.random() * 25);
    const accentLight = 45 + Math.floor(Math.random() * 20);
    
    return {
      bg: `hsl(${chosenHue}, ${bgSat}%, ${bgLight}%)`,
      surface: `hsl(${chosenHue}, ${bgSat + 8}%, ${bgLight + 10}%)`,
      text: `hsl(${chosenHue}, ${textSat}%, ${82 + Math.floor(Math.random() * 10)}%)`,
      textMuted: `hsl(${chosenHue}, ${Math.floor(Math.random() * 15)}%, ${55 + Math.floor(Math.random() * 10)}%)`,
      accent: `hsl(${(chosenHue + 180) % 360}, ${accentSat}%, ${accentLight}%)`,
      border: `hsl(${chosenHue}, ${bgSat + 5}%, ${bgLight + 15}%)`,
      primary: `hsl(${chosenHue}, ${textSat}%, ${78 + Math.floor(Math.random() * 12)}%)`,
    };
  } else {
    // Light themes with complementary accents
    const accentHue = (chosenHue + 180) % 360;
    const accentSat = 65 + Math.floor(Math.random() * 30);
    const textSat = 35 + Math.floor(Math.random() * 20);
    
    return {
      bg: "#FFFFFF",
      surface: `hsl(${chosenHue}, 20%, ${94 + Math.floor(Math.random() * 4)}%)`,
      text: `hsl(${chosenHue}, ${textSat}%, ${10 + Math.floor(Math.random() * 8)}%)`,
      textMuted: `hsl(${chosenHue}, ${Math.floor(Math.random() * 20)}%, ${40 + Math.floor(Math.random() * 10)}%)`,
      accent: `hsl(${accentHue}, ${accentSat}%, ${42 + Math.floor(Math.random() * 15)}%)`,
      border: `hsl(${chosenHue}, 15%, ${85 + Math.floor(Math.random() * 8)}%)`,
      primary: `hsl(${chosenHue}, ${textSat}%, ${12 + Math.floor(Math.random() * 10)}%)`,
    };
  }
};

const ThemeCreatorModal = ({ onClose, onSave, onOpenExtractor }) => {
  const [name, setName] = useState("");
  const [colors, setColors] = useState(generateRandomPalette());
  const [generationCount, setGenerationCount] = useState(1);
  const setC = (k, v) => setColors((p) => ({ ...p, [k]: v }));
  const labels = {
    bg: "Background",
    surface: "Surface",
    text: "Text",
    textMuted: "Muted Text",
    accent: "Accent",
    border: "Border",
    primary: "Primary",
  };

  const applyPreset = (preset) => {
    setColors(preset.colors);
    if (!name.trim() || name.includes("Generated")) {
      setName(preset.name);
    }
  };

  const handleAutoGenerate = () => {
    const autoGen = generateRandomPalette();
    setColors(autoGen);
    setGenerationCount((prev) => prev + 1);
    // Auto-generate a name if empty
    if (!name.trim() || name.includes("Generated")) {
      const themeNames = [
        "Midnight Luxe",
        "Sunset Glory",
        "Ocean Dreams",
        "Forest Mystique",
        "Desert Gold",
        "Violet Dreams",
        "Cosmic Shine",
        "Emerald Bliss",
        "Rose Elegance",
        "Arctic Frost",
      ];
      const randomName =
        themeNames[Math.floor(Math.random() * themeNames.length)];
      setName(`${randomName} (Generated ${generationCount + 1})`);
    }
  };

  return (
    <div className="theme-creator-modal-overlay" onClick={onClose}>
      <div className="theme-creator-modal" onClick={(e) => e.stopPropagation()}>
        <div className="theme-creator-header">
          <h3>🎨 Create Custom Theme</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {onOpenExtractor && (
              <button
                type="button"
                className="extract-theme-btn"
                style={{ padding: "4px 10px", height: 30, fontSize: "0.72rem" }}
                onClick={() => {
                  onClose();
                  onOpenExtractor();
                }}
              >
                <Sparkles size={12} />
                <span>Extract from Image</span>
              </button>
            )}
            <button
              className="theme-creator-close-btn"
              onClick={onClose}
              type="button"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="theme-creator-body">
          <div className="theme-creator-input-group">
            <label>Theme Name</label>
            <input
              className="theme-creator-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Ocean Midnight"
            />
          </div>

          {/* Auto Theme Generator & Presets */}
          <div className="auto-theme-generator-bar">
            <div className="auto-gen-header">
              <label>✨ Auto Palette Generator</label>
              <button
                type="button"
                className="auto-gen-btn"
                onClick={handleAutoGenerate}
              >
                🎲 Auto Generate Palette
              </button>
            </div>

            <div className="preset-palettes-row">
              {PRESET_PALETTES.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  className="preset-palette-chip"
                  onClick={() => applyPreset(p)}
                  title={`Apply ${p.name}`}
                  style={{
                    backgroundColor: p.colors.bg,
                    borderColor: p.colors.accent,
                  }}
                >
                  <span
                    className="preset-dot"
                    style={{ backgroundColor: p.colors.accent }}
                  />
                  <span
                    className="preset-name"
                    style={{ color: p.colors.text }}
                  >
                    {p.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="theme-creator-preview">
            <h4>Live Preview</h4>
            <div
              style={{
                background: colors.bg,
                border: `2px solid ${colors.accent}`,
                borderRadius: 8,
                padding: "12px 16px",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  color: colors.text,
                  marginBottom: 4,
                }}
              >
                {name || "Theme Name"}
              </div>
              <div
                style={{
                  fontSize: "0.72rem",
                  color: colors.textMuted,
                  marginBottom: 8,
                }}
              >
                22K Gold · 6.5g · Hallmarked
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <div
                  style={{
                    background: colors.accent,
                    color: colors.bg,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    padding: "4px 12px",
                    borderRadius: 4,
                  }}
                >
                  ₹95,000
                </div>
                <div
                  style={{
                    background: colors.surface,
                    border: `1px solid ${colors.border}`,
                    fontSize: "0.65rem",
                    color: colors.textMuted,
                    padding: "4px 12px",
                    borderRadius: 4,
                  }}
                >
                  HALLMARKED
                </div>
              </div>
            </div>
          </div>

          <div className="theme-creator-color-grid">
            {Object.entries(labels).map(([key, label]) => (
              <div key={key} className="theme-creator-color-row">
                <label>{label}</label>
                <div className="theme-creator-color-input">
                  <input
                    type="color"
                    value={colors[key]}
                    onChange={(e) => setC(key, e.target.value)}
                    className="theme-creator-color-picker"
                  />
                  <span className="theme-creator-color-value">
                    {colors[key]}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="theme-creator-footer">
          <div className="footer-actions">
            <button
              className="theme-creator-btn theme-creator-cancel-btn"
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="theme-creator-btn theme-creator-save-btn"
              type="button"
              onClick={() => {
                if (!name.trim()) {
                  alert("Please enter a theme name");
                  return;
                }
                onSave({
                  id: "custom-" + Date.now(),
                  name: name.trim(),
                  catalogueTheme: "pearl",
                  colors,
                });
              }}
              title="Save and apply this as a new existing theme"
            >
              <Save size={14} /> Save & Apply Theme
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══ Auto-save toast ══ */
const AutoSaveToast = ({ visible }) => (
  <div
    style={{
      position: "fixed",
      bottom: 20,
      right: 20,
      zIndex: 2000,
      display: "flex",
      alignItems: "center",
      gap: 8,
      background: "var(--color-bg,#fff)",
      border: "1px solid var(--color-border,#e2e8f0)",
      borderRadius: 8,
      padding: "8px 14px",
      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
      fontSize: "0.8rem",
      fontWeight: 600,
      color: "var(--color-text,#0f172a)",
      transition: "opacity 0.3s ease, transform 0.3s ease",
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(10px)",
      pointerEvents: "none",
    }}
  >
    <CheckCircle size={14} color="#22c55e" /> Design auto-saved
  </div>
);

/* ══════════════════ MAIN ══════════════════ */
const VisualDesignerStudio = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { segment: urlSeg } = useParams();
  const { buyer, updateBuyer, savePosterDesign } = useBuyer();

  const incomingProduct = location.state?.product;
  const downloadOnLoad = location.state?.downloadOnLoad;

  /* ── resolve product ── */
  const [currentProduct, setCurrentProduct] = useState(null);
  useEffect(() => {
    if (incomingProduct) {
      setCurrentProduct(incomingProduct);
      return;
    }
    if (urlSeg) {
      const p = carddemo.find((p) => p.segment === urlSeg);
      if (p) {
        setCurrentProduct(p);
        return;
      }
    }
    if (carddemo.length) setCurrentProduct(carddemo[0]);
  }, [incomingProduct, urlSeg]);

  /* ── design state — loaded AFTER product is known ── */
  const [activeTheme, setActiveTheme] = useState("light-studio");
  const [activeFontPair, setActiveFontPair] = useState("modern-clean");
  const [activeLayout, setActiveLayout] = useState("editorial-catalogue");
  const [visibleFields, setVisibleFields] = useState({});
  const [designLoaded, setDesignLoaded] = useState(false);

  /* Load saved design once product segment is known */
  useEffect(() => {
    if (!currentProduct) return;
    const seg = currentProduct.segment;
    const saved = buyer?.posterDesigns?.[seg] || buyer?.cardDesign || {};
    if (saved.activeTheme) setActiveTheme(saved.activeTheme);
    if (saved.activeFontPair) setActiveFontPair(saved.activeFontPair);
    if (saved.activeLayout) setActiveLayout(saved.activeLayout);
    if (saved.visibleFields) setVisibleFields(saved.visibleFields);
    setDesignLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProduct?.segment]); // only re-run when segment changes, not on every buyer update

  const [customThemes, setCustomThemes] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("jmi-custom-themes") || "[]");
    } catch {
      return [];
    }
  });

  const [activeSelector, setActiveSelector] = useState("theme");
  const [showThemeCreator, setShowThemeCreator] = useState(false);
  const [showExtractor, setShowExtractor] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);

  const layoutRef = useRef(null);
  const saveTimerRef = useRef(null);

  /* ── derived configs ── */
  const allThemes = useMemo(
    () => [...designerConfig.themes, ...customThemes],
    [customThemes],
  );

  const themeConfig = useMemo(
    () =>
      allThemes.find((t) => t.id === activeTheme) || designerConfig.themes[0],
    [activeTheme, allThemes],
  );

  const fontConfig = useMemo(
    () =>
      designerConfig.fontPairs.find((f) => f.id === activeFontPair) ||
      designerConfig.fontPairs[0],
    [activeFontPair],
  );

  /* ── CSS vars ── */
  const cssVars = useMemo(
    () => ({
      "--theme-bg": themeConfig.colors.bg,
      "--theme-surface": themeConfig.colors.surface,
      "--theme-text": themeConfig.colors.text,
      "--theme-textMuted": themeConfig.colors.textMuted,
      "--theme-border": themeConfig.colors.border,
      "--theme-accent": themeConfig.colors.accent,
      "--theme-primary": themeConfig.colors.primary,
      "--theme-heading-font": `"${
        fontConfig?.heading || "Montserrat"
      }", sans-serif`,
      "--theme-body-font": `"${fontConfig?.body || "Open Sans"}", sans-serif`,
    }),
    [themeConfig, fontConfig],
  );

  /* ── AUTO-SAVE per segment ── */
  const triggerSave = useCallback(() => {
    if (!designLoaded) return; // don't save until initial load is done
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const seg = currentProduct?.segment || urlSeg || "gold";
      const design = {
        activeTheme,
        activeFontPair,
        activeLayout,
        visibleFields,
        savedAt: Date.now(),
      };
      if (savePosterDesign) savePosterDesign(seg, design);
      updateBuyer({ cardDesign: { ...(buyer?.cardDesign || {}), ...design } });
      setToastVisible(true);
      setTimeout(() => setToastVisible(false), 2200);
    }, 700);
  }, [
    designLoaded,
    activeTheme,
    activeFontPair,
    activeLayout,
    visibleFields,
    currentProduct,
    urlSeg,
    savePosterDesign,
    updateBuyer,
    buyer?.cardDesign,
  ]);

  useEffect(() => {
    triggerSave();
  }, [triggerSave]);
  useEffect(() => () => clearTimeout(saveTimerRef.current), []);

  /* ── Handle download on load ── */
  useEffect(() => {
    if (downloadOnLoad && designLoaded && layoutRef.current) {
      // Wait a moment for layout to fully render
      const timer = setTimeout(async () => {
        try {
          // layoutRef.current IS the card-preview-wrapper div with the poster content
          // It's 420×560px and contains all the rendered design
          const posterElement = layoutRef.current;
          
          if (!posterElement) {
            console.error("Poster element not found");
            return;
          }

          // Try to use html2canvas if available
          if (typeof window !== "undefined" && window.html2canvas) {
            const canvas = await window.html2canvas(posterElement, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              backgroundColor: themeConfig.colors?.bg || "#FFFFFF",
              logging: false,
              imageTimeout: 15000,
              // Don't capture outside the element bounds
              width: posterElement.offsetWidth,
              height: posterElement.offsetHeight,
            });

            // Download the canvas
            canvas.toBlob((blob) => {
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              const seg = currentProduct?.segment || "design";
              const name = currentProduct?.title || currentProduct?.productName || "poster";
              link.download = `${seg}_${name}_${Date.now()}.png`;
              document.body.appendChild(link);
              link.click();
              URL.revokeObjectURL(url);
              document.body.removeChild(link);
              setToastVisible(true);
            });
          } else {
            alert("Design is ready! Use Ctrl+Shift+S (Windows) or Cmd+Shift+4 (Mac) to capture the exact design image.");
          }
        } catch (err) {
          console.error("Download error:", err);
          alert("Could not download. Please use browser screenshot (Ctrl+Shift+S).");
        }
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [downloadOnLoad, designLoaded, layoutRef, currentProduct, themeConfig, setToastVisible]);

  /* ── custom theme ── */
  const saveCustomTheme = (theme) => {
    const updated = [...customThemes, theme];
    setCustomThemes(updated);
    try {
      localStorage.setItem("jmi-custom-themes", JSON.stringify(updated));
    } catch {}
    
    // Always apply the theme immediately (treat as existing theme)
    setActiveTheme(theme.id);
    
    // Save the applied theme to poster designs so it's visible in settings
    const seg = currentProduct?.segment || urlSeg || "gold";
    const design = {
      activeTheme: theme.id,
      activeFontPair,
      activeLayout,
      visibleFields,
      appliedTheme: {
        id: theme.id,
        name: theme.name,
        colors: theme.colors,
        appliedAt: Date.now(),
      },
      savedAt: Date.now(),
    };
    if (savePosterDesign) savePosterDesign(seg, design);
    updateBuyer({ cardDesign: { ...(buyer?.cardDesign || {}), ...design } });
    
    setShowThemeCreator(false);
  };

  const handleApplyExtractedTheme = (theme) => {
    const updated = [...customThemes, theme];
    setCustomThemes(updated);
    try {
      localStorage.setItem("jmi-custom-themes", JSON.stringify(updated));
    } catch {}
    setActiveTheme(theme.id);
    if (theme.fontPairId) setActiveFontPair(theme.fontPairId);
    
    // Save applied theme to poster designs
    const seg = currentProduct?.segment || urlSeg || "gold";
    const design = {
      activeTheme: theme.id,
      activeFontPair: theme.fontPairId || activeFontPair,
      activeLayout,
      visibleFields,
      appliedTheme: {
        id: theme.id,
        name: theme.name,
        colors: theme.colors,
        source: "brandExtractor",
        appliedAt: Date.now(),
      },
      savedAt: Date.now(),
    };
    if (savePosterDesign) savePosterDesign(seg, design);
    updateBuyer({ cardDesign: { ...(buyer?.cardDesign || {}), ...design } });
    
    setShowExtractor(false);
    setShowThemeCreator(false);
  };

  const deleteCustomTheme = (id) => {
    const updated = customThemes.filter((t) => t.id !== id);
    setCustomThemes(updated);
    try {
      localStorage.setItem("jmi-custom-themes", JSON.stringify(updated));
    } catch {}
    if (activeTheme === id) setActiveTheme("light-studio");
  };

  const toggleField = (key) =>
    setVisibleFields((p) => ({ ...p, [key]: !p[key] }));

  /* ── segment attr keys ── */
  const segmentAttrKeys = useMemo(() => {
    if (!currentProduct) return [];
    const seg = currentProduct.segment?.toLowerCase();
    const keys =
      SEGMENT_ATTR_KEYS[seg] || Object.keys(currentProduct.attributes || {});
    return keys.filter((k) => k in (currentProduct.attributes || {}));
  }, [currentProduct]);

  /* ── zoom / scale state for PC studio workbench ── */
  const [cardScale, setCardScale] = useState(90);
  const [dockCollapsed, setDockCollapsed] = useState(false);

  /* ── loading guard ── */
  if (!currentProduct) {
    return (
      <div className="designer-page">
        <Header title="Visual Designer" showBack onBack={() => navigate(-1)} />
        <div className="designer-loading">Loading product…</div>
      </div>
    );
  }

  const LayoutComponent =
    LAYOUTS[activeLayout] || LAYOUTS["editorial-catalogue"];
  const defaultThemes = designerConfig.themes;

  const handleToggleAllAttrs = (show) => {
    const updated = { ...visibleFields };
    segmentAttrKeys.forEach((key) => {
      updated[key] = show;
    });
    setVisibleFields(updated);
  };

  return (
    <div className="designer-page">
      <Header
        title="Visual Designer Studio"
        showBack
        onBack={() => navigate(-1)}
      />

      {showThemeCreator && (
        <ThemeCreatorModal
          onClose={() => setShowThemeCreator(false)}
          onSave={saveCustomTheme}
          onOpenExtractor={() => setShowExtractor(true)}
        />
      )}

      {showExtractor && (
        <BrandThemeExtractor
          onClose={() => setShowExtractor(false)}
          onApplyTheme={handleApplyExtractedTheme}
        />
      )}

      <AutoSaveToast visible={toastVisible} />

      <div className="designer-main-container">
        {/* ══ TOP STUDIO TOOLBAR ══ */}
        <div className="designer-topbar">
          <div className="topbar-left">
            <div className="selector-tabs">
              {[
                { id: "layout", icon: "📐", label: "Layout" },
                { id: "theme", icon: "🎨", label: "Theme" },
                { id: "font", icon: "🔤", label: "Font" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  className={`selector-tab ${
                    activeSelector === tab.id ? "active" : ""
                  }`}
                  onClick={() => setActiveSelector(tab.id)}
                  type="button"
                >
                  <span className="tab-icon">{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="selector-content">
            {activeSelector === "layout" && (
              <div className="layout-selector-group">
                <LayoutSelector
                  activeLayout={activeLayout}
                  onLayoutChange={setActiveLayout}
                />
              </div>
            )}

            {activeSelector === "theme" && (
              <div className="theme-side-bar">
                <button
                  className="extract-theme-btn"
                  onClick={() => setShowExtractor(true)}
                  type="button"
                  title="Extract theme from logo or visiting card"
                >
                  <Sparkles size={14} />
                  <span>Extract Brand</span>
                </button>

                {defaultThemes.map((th) => (
                  <button
                    key={th.id}
                    className={`theme-chip-btn ${
                      activeTheme === th.id ? "active" : ""
                    }`}
                    onClick={() => setActiveTheme(th.id)}
                    title={th.name}
                    type="button"
                  >
                    {/* Gradient swatch strip — 3 colors side by side */}
                    <span
                      className="theme-chip-swatch"
                      style={{
                        background: `radial-gradient(circle at center, ${th.colors.bg} 0%, ${th.colors.accent} 45%, ${th.colors.surface} 100%)`,
                        border: `1px solid ${
                          activeTheme === th.id
                            ? th.colors.accent
                            : "rgba(255,255,255,0.12)"
                        }`,
                      }}
                    />
                  </button>
                ))}

                {customThemes.map((th) => (
                  <div key={th.id} className="theme-chip-wrapper">
                    <button
                      className={`theme-chip-btn ${
                        activeTheme === th.id ? "active" : ""
                      }`}
                      onClick={() => setActiveTheme(th.id)}
                      title={th.name}
                      type="button"
                    >
                      <span
                        className="theme-chip-swatch"
                        style={{
                          background: `radial-gradient(circle at center, ${th.colors.bg} 0%, ${th.colors.accent} 45%, ${th.colors.surface} 100%)`,
                          border: `1px solid ${
                            activeTheme === th.id
                              ? th.colors.accent
                              : "rgba(255,255,255,0.12)"
                          }`,
                        }}
                      />
                    </button>
                    <button
                      className="delete-custom-theme"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteCustomTheme(th.id);
                      }}
                      type="button"
                      aria-label={`Delete ${th.name}`}
                    >
                      <Trash2 size={9} />
                    </button>
                  </div>
                ))}

                <button
                  className="add-theme-btn"
                  onClick={() => setShowThemeCreator(true)}
                  type="button"
                  title="Create custom theme"
                >
                  <Plus size={14} />
                  <span>New Theme</span>
                </button>
              </div>
            )}

            {activeSelector === "font" && (
              <div className="font-pair-selector-group">
                <FontPairSelector
                  activeFontPair={activeFontPair}
                  onFontPairChange={setActiveFontPair}
                  fontPairs={designerConfig.fontPairs}
                />
              </div>
            )}
          </div>
        </div>

        {/* ══ WORKSPACE: CANVAS & CONTROL DOCK ══ */}
        <div className="designer-workspace-body">
          {/* ══ CANVAS ══ */}
          <div className="designer-canvas">
            <div className="canvas-workbench-header">
              <div className="workbench-info">
                <span className="workbench-segment-tag">
                  {currentProduct.segment?.toUpperCase() || "CATALOGUE"}
                </span>
                <span className="workbench-product-name">
                  {currentProduct.title || "Poster Preview"}
                </span>
              </div>

              <div className="workbench-zoom-controls">
                <button
                  type="button"
                  className="zoom-btn"
                  onClick={() => setCardScale((s) => Math.max(60, s - 10))}
                  title="Zoom out"
                >
                  −
                </button>
                <span className="zoom-value">{cardScale}%</span>
                <button
                  type="button"
                  className="zoom-btn"
                  onClick={() => setCardScale((s) => Math.min(150, s + 10))}
                  title="Zoom in"
                >
                  +
                </button>
                {cardScale !== 100 && (
                  <button
                    type="button"
                    className="zoom-reset-btn"
                    onClick={() => setCardScale(100)}
                  >
                    Reset
                  </button>
                )}
                <button
                  type="button"
                  className={`studio-expand-btn ${
                    dockCollapsed ? "expanded" : ""
                  }`}
                  onClick={() => setDockCollapsed((prev) => !prev)}
                  title={
                    dockCollapsed
                      ? "Show Studio Inspector"
                      : "Expand Canvas / Hide Inspector"
                  }
                >
                  {dockCollapsed ? "⇤ Inspector" : "⛶ Expand Studio"}
                </button>
              </div>
            </div>

            <div
              className="canvas-card-wrapper"
              style={{
                transform: `scale(${cardScale / 100})`,
                transformOrigin: "top center",
                transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <LayoutComponent
                ref={layoutRef}
                product={currentProduct}
                buyer={buyer}
                activeTheme={activeTheme}
                activeFontPair={activeFontPair}
                cardScale={cardScale}
                visibleFields={visibleFields}
                cssVars={cssVars}
                forExport={false}
              />
            </div>
          </div>

          {/* ══ CONTROL DOCK (PC INSPECTOR) ══ */}
          <div className={`control-dock ${dockCollapsed ? "collapsed" : ""}`}>
            <div className="dock-header">
              <span className="dock-header-title">Studio Inspector</span>
              <span className="dock-save-badge">Auto-Saved</span>
            </div>

            {/* Segment attributes with show/hide toggle chips */}
            <div className="control-group dock-section">
              <div className="dock-section-header">
                <label className="dock-section-label">
                  {currentProduct.segment
                    ? currentProduct.segment.charAt(0).toUpperCase() +
                      currentProduct.segment.slice(1)
                    : "Product"}{" "}
                  Attributes
                </label>
                <div className="toggle-all-btns">
                  <button
                    type="button"
                    className="toggle-all-btn"
                    onClick={() => handleToggleAllAttrs(true)}
                  >
                    Show All
                  </button>
                  <button
                    type="button"
                    className="toggle-all-btn"
                    onClick={() => handleToggleAllAttrs(false)}
                  >
                    Hide All
                  </button>
                </div>
              </div>
              <div className="attr-chip-grid">
                {segmentAttrKeys.map((key) => {
                  const val = currentProduct.attributes[key];
                  const isShown = visibleFields[key] !== false; // default visible
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`attr-chip ${
                        isShown ? "attr-chip-on" : "attr-chip-off"
                      }`}
                      onClick={() =>
                        setVisibleFields((p) => ({ ...p, [key]: !isShown }))
                      }
                      title={isShown ? "Click to hide" : "Click to show"}
                    >
                      <span className="attr-chip-key">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span className="attr-chip-val">{fmtAttr(val)}</span>
                      <span className="attr-chip-eye">
                        {isShown ? "👁" : "🚫"}
                      </span>
                    </button>
                  );
                })}

                {/* Price chip */}
                {(currentProduct.pricing?.retail ||
                  currentProduct.attributes?.retail_price) && (
                  <div className="attr-chip attr-chip-price">
                    <span className="attr-chip-key">Price</span>
                    <span className="attr-chip-val">
                      {fmtPrice(
                        currentProduct.pricing?.retail ||
                          currentProduct.attributes?.retail_price,
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Buyer info visibility */}
            <div className="control-group dock-section">
              <label className="dock-section-label">
                Buyer Info Visibility
              </label>
              <div className="attr-chip-grid">
                {[
                  { key: "logoUrl", label: "Logo" },
                  { key: "organizationName", label: "Brand Name" },
                  { key: "address", label: "Address" },
                  { key: "phone", label: "Phone" },
                ].map(({ key, label }) => {
                  const isShown = visibleFields[key] !== false;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`attr-chip ${
                        isShown ? "attr-chip-on" : "attr-chip-off"
                      }`}
                      onClick={() => toggleField(key)}
                    >
                      <span className="attr-chip-key">{label}</span>
                      <span className="attr-chip-eye">
                        {isShown ? "👁" : "🚫"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualDesignerStudio;
